/**
 * Sesotho: Compose
 *
 * What this file covers: Writing a single message to one person or a few, outside a conversation.
 *
 * Tone: Friendly and direct. Make clear who will receive the message and on which channel.
 *
 * Sesotho: Everyday Sesotho as used in South African business. Keys not translated yet fall back to English, so
 * a missing key is never an error.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const compose: Catalog = {
  'compose.title': 'Molaetsa o mocha',
  'compose.description': 'Romela molaetsa ho motho a le mong, kapa ho ba seng bakae. Ba khethe ka lebitso kapa ka nomoro, mme karabo e tla fihla lebokoseng la hao.',
  'compose.step.who': 'E ea ho mang',
  'compose.step.what': 'E reng',
  'compose.step.send': 'Litšenyehelo le ho romela',
  'compose.number.label': 'Romela ho tswa ho',
  'compose.number.hint': 'Nomoro eo molaetsa ona o tswang ho eona. Karabo e fihla lebokoseng la nomoro eo.',
  'compose.recipients.label': 'Ho',
  'compose.recipients.hint': 'Batla lebitso kapa nomoro, kapa ngola nomoro e so bolokoang.',
  'compose.recipients.placeholder': 'Lebitso kapa nomoro',
  'compose.recipients.remove': 'Tlosa {{who}}',
  'compose.recipients.notInBook': 'Ha a eso be har\'a bao u ikopanyang le bona',
  'compose.recipients.noName': 'Ha ho lebitso le bolokoang',
  'compose.recipients.noMatches': 'Ha ho motho ea tsamaellanang le seo. Ngola nomoro e felletseng ho romela ho ea mocha.',
  'compose.recipients.count': 'Batho ba {{count}} ho ba {{max}} ba khethiloe',
  'compose.recipients.clear': 'Hlakola bohle',
  'compose.recipients.full.title': 'Ke bakalo feela bao mona a ba amohelang',
  'compose.recipients.full.body': 'Molaetsa o ngotsoeng mona o ea ho batho ba sa feteng {{max}}. Haeba ba le bangata ho feta moo, romela phatlalatso ho letšoao kapa ho karolo.',
  'compose.window.badge.open': 'A ka amohela molaetsa',
  'compose.window.badge.needsTemplate': 'O hloka mohlala',
  'compose.window.badge.templateOnly': 'Mohlala feela',
  'compose.window.badge.optedOut': 'O tsoile',
  'compose.window.why.openHours': 'O u ngoletse, ho setse lihora tse {{hours}} tsa ho araba ka mantsoe a hao',
  'compose.window.why.openMinutes': 'O u ngoletse, ho setse metsotso e {{minutes}} ea ho araba ka mantsoe a hao',
  'compose.window.why.never': 'Ha a eso ke a u ngolla',
  'compose.window.why.expiredDays': 'O qetetse ho utloa ho eena matsatsing a {{days}} a fetileng',
  'compose.window.why.expiredHours': 'O qetetse ho utloa ho eena lihoreng tse {{hours}} tse fetileng',
  'compose.window.why.optedOut': 'O kopile ho se amohele melaetsa',
  'compose.window.closed.title': 'Ona o hloka mohlala o amohetsoeng',
  'compose.window.closed.body': 'WhatsApp e lumella molaetsa ka mantsoe a hao feela lihoreng tse 24 ka mor\'a hore motho a u ngolle. Khetha mohlala o amohetsoeng ho e-na le hoo.',
  'compose.window.mixed.title': 'Batho bana ha ba maemong a tšoanang',
  'compose.window.mixed.body': 'Ba {{closed}} ho ba {{total}} ha ba a ka ba u ngolla lihoreng tse 24 tse fetileng, mme molaetsa o le mong o ea ho bona bohle, kahoo ona o tlameha ho ba mohlala o amohetsoeng.',
  'compose.window.optedOut': 'Ba {{count}} ho ba khethiloeng ba kopile ho se amohele melaetsa. Ba siiloe mme ha ba lefisoe.',
  'compose.message.label': 'Molaetsa',
  'compose.message.hint': 'Ka mantswe a hao. Tlanya {{ ho eketsa ho hong ka motho ka mong, jwalo ka lebitso la hae.',
  'compose.message.placeholder': 'Ngola molaetsa oa hao',
  'compose.message.count': '{{length}} ho {{max}}',
  'compose.message.previewEmpty': 'Molaetsa oa hao o tla bonahala mona',
  'compose.message.previewBusiness': 'Khoebo ea hao',
  'compose.template.label': 'Mohlala',
  'compose.template.hint': 'Ke mehlala e amohetsoeng ke WhatsApp feela e ka romeloang ka ntle ho fensetere ea lihora tse 24.',
  'compose.template.placeholders': 'Tlatsa libaka tse se nang letho',
  'compose.template.placeholders.hint': 'Tlanya {{ ho tlatsa sekgeo ho tswa kareteng ya mokopani ya motho ka mong, jwalo ka lebitso la hae. Motho ka mong o fumana boleng ba hae.',
  'compose.template.placeholder': 'Sebaka {{n}}',
  'compose.template.noPlaceholders': 'Mohlala ona ha o na libaka tse lokelang ho tlatsoa.',
  'compose.template.noneApproved.title': 'Ha ho mehlala e amohetsoeng hajoale',
  'compose.template.noneApproved.body': 'WhatsApp e tlameha ho amohela mohlala pele o ka romeloa ho motho ea sa kang a u ngolla haufinyane.',
  'compose.template.noneApproved.action': 'Theha mohlala',
  'compose.template.devFallbackHidden': 'Mehlala e {{count}} e patiloe hobane WhatsApp e eso e amohele.',
  'compose.summary.recipients': 'Batho',
  'compose.summary.excluded': 'Ba {{count}} ba siiloe',
  'compose.summary.cost': 'Sena se tla bitsa',
  'compose.summary.balance': 'Chelete e setseng',
  'compose.summary.balanceAfter': '{{after}} e setseng ka mor\'a ho romela',
  'compose.summary.balanceUnknown': 'Khetha hore e ea ho mang, mme re tla e bala.',
  'compose.summary.short.title': 'Litokelo ha li lekane',
  'compose.summary.short.body': 'O hloka {{shortfall}} ho feta ho romela sena.',
  'compose.summary.short.action': 'Kenya chelete',
  'compose.summary.broadcastHint': 'Na u romela ho batho ba bangata?',
  'compose.summary.broadcastAction': 'Romela phatlalatso',
  'compose.action.send': 'Romela ho ba {{count}}',
  'compose.action.sendTemplate': 'Romela mohlala ho ba {{count}}',
  'compose.error.bodyRequired': 'Ngola molaetsa pele u o romela.',
  'compose.error.templateRequired': 'Khetha mohlala o amohetsoeng.',
  'compose.error.placeholderRequired': 'Tlatsa sena pele u romela.',
  'compose.error.numbersFailed': 'Linomoro tsa hao tsa WhatsApp li ke ke tsa jareloa hajoale.',
  'compose.error.noNumbers.title': 'Ha ho nomoro ea WhatsApp e hokahantsoeng',
  'compose.error.noNumbers.body': 'Molaetsa o tlameha ho tsoa nomorong e hokahantsoeng. Hokahanya e le \'ngoe, mme u tla khona ho romela ho mang kapa mang.',
  'compose.error.noNumbers.action': 'Hokahanya nomoro',
  'compose.refusal.windowClosed': 'Ba bang ba batho bana ha ba a ka ba u ngolla lihoreng tse 24 tse fetileng, kahoo ona o tlameha ho ba mohlala o amohetsoeng. Ha ho letho le romeloang.',
  'compose.refusal.TOO_MANY_RECIPIENTS': 'Ke batho ba bangata ho feta bao molaetsa o ngotsoeng mona o ba amohelang. Romela phatlalatso ho letšoao kapa ho karolo ho e-na le hoo.',
  'compose.refusal.NUMBER_REQUIRED': 'Bolela hore na molaetsa ona o tsoa nomorong efe ea hao.',
  'compose.refusal.NO_ACTIVE_NUMBER': 'Ha ho nomoro ea WhatsApp e hokahantsoeng, kahoo ha ho letho le ka romeloang.',
  'compose.refusal.VALIDATION': 'Ho na le ntho e sa tlatsoang molaetseng ona.',
  'compose.refusal.unknown': 'Sena se ke ke sa romeloa hajoale. Leka hape ka mor\'a motsotsoana.',
  'compose.toast.sent': 'E romeletsoe ho ba {{count}}.',
  'compose.toast.partial': 'E romeletsoe ho ba {{sent}}, empa ba {{failed}} ha ba a ka ba fumana.',
  'compose.toast.noneSent': 'Ha ho letho le romeloang. Sheba ka tlase hore na ho etsahetseng.',
  'compose.results.allSent.title': 'Tsohle li romeloe',
  'compose.results.allSent.body': 'Sena se fihlile ho bohle ba {{count}}. Likarabo tsa bona li tla fihla lebokoseng la hao.',
  'compose.results.noneSent.title': 'Ha ho letho le romeloang',
  'compose.results.noneSent.body': 'Ha ho motho ho ba {{count}} ea fihletsoeng. Se ileng sa fosahala se bontšoa pel\'a motho e mong le e mong.',
  'compose.results.partial.title': 'E meng ea melaetsa ena ha ea ka ea fihla',
  'compose.results.partial.body': 'Ba {{sent}} ba fihletsoe mme ba {{failed}} ha ba a fihleloa. U ka leka hape bao ba hlolehileng ntle le ho romela habeli ho mang kapa mang.',
  'compose.results.delivered': 'E tseleng',
  'compose.results.open': 'Bula moqoqo',
  'compose.results.badge.sent': 'E romeloe',
  'compose.results.badge.failed': 'Ha ea romeloa',
  'compose.results.backToInbox': 'Khutlela lebokoseng',
  'compose.results.another': 'Ngola o mong',
  'compose.results.retry': 'Leka ba {{count}} hape',
  'compose.results.reason.unknown': 'Ona ha oa ka oa fihla.',
  'compose.results.reason.OPTED_OUT': 'Ba kopile ho se amohele melaetsa.',
  'compose.results.reason.WINDOW_CLOSED': 'Ha ba a ka ba u ngolla lihoreng tse 24 tse fetileng, kahoo sena se ne se hloka mohlala o amohetsoeng.',
  'compose.results.reason.INSUFFICIENT_CREDITS': 'Litokelo li ne li sa lekana ho romela ona.',
  'compose.results.reason.PHONE_NOT_FOUND': 'Nomoro eo sena se romeloang ho tsoa ho eona ha e sa hokahantsoe.',
  'compose.results.reason.CHANNEL_UNREACHABLE': 'WhatsApp e ne e sa fihlelehe. Ha ho letho le romeloang ho bona.',
  'compose.results.reason.SEND_FAILED': 'WhatsApp ha ea ka ea amohela ona.',
  'compose.results.reason.META_REFUSED': 'WhatsApp ha ea ka ea amohela ona.',
  'compose.results.reason.SEND_UNREADABLE': 'Sena se tsoile empa ha rea ka ra tiisa hore na ho etsahetseng ka sona.',
  'compose.number.unnamed': 'Nomoro e ntseng e hlophisoa',
  'compose.results.reason.ACCOUNT_NOT_PAYABLE': 'WhatsApp e e hanne ka lebaka la bothata ba tefo lehlakoreng la rona, e seng la hao. Re a bo lokisa, mme ha ho hlokahale hore o etse letho ho Meta.',
  'compose.results.reason.UNDELIVERABLE': 'WhatsApp e hlolehile ho e isa nomorong eo.',
  'compose.results.reason.NUMBER_NOT_REGISTERED': 'Nomoro eo u rometseng ka eona ha e ngolisoa ho WhatsApp.',
  'compose.results.reason.MEDIA_NOT_FETCHED': 'WhatsApp e hlolehile ho lata faele e khomaretsoeng.'
};

export default compose;
